<template>
  <div class="multi-country-switcher__content">
    <!-- Custom close button to bind custom close event -->
    <div class="multi-country-switcher-overlay__header overlay__header">
      <p
        id="overlay-multiCountrySwitcher"
        class="visually-hidden"
        v-text="$string('general.multi_country.title')"
      />

      <div
        class="
          multi-country-switcher-overlay__close-container
          overlay__close-container
        "
      >
        <button
          class="multi-country-switcher-overlay__close overlay__close icon-button"
          type="button"
          @click.prevent="handleCloseOverlay"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.close')"
          />

          <icon-close />
        </button>
      </div>
    </div>

    <div class="multi-country-switcher__body">
      <!-- Options -->
      <div
        v-if="template === 'options'"
        class="multi-country-switcher__options"
      >
        <div class="multi-country-switcher__header text-center">
          <p
            class="
              multi-country-switcher__options-title
              text-heading-l
            "
            v-text="$string('general.multi_country.options.title')"
          />
        </div>

        <multi-country-options
          class="multi-country-switcher__options-selects"
          :current-instance="currentInstance"
          :instances="instances"
          :suggested-instance="suggestedInstance"
          @update-selected-instance="updateSelectedInstance"
        />
      </div>

      <!-- Suggestions -->
      <div
        v-else
        class="multi-country-switcher__suggestions"
      >
        <div class="multi-country-switcher__header text-center">
          <p
            class="
              multi-country-switcher__suggestions-title
              text-heading-3xl
              text-heading-4xl-desktop
            "
            v-html="suggestionStrings.title"
          />

          <p
            class="
              multi-country-switcher__suggestions-text
              text-body-m-regular
            "
            v-html="suggestionStrings.text"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="multi-country-switcher__footer">
        <btn
          v-if="suggestedInstance || template === 'options'"
          class="multi-country-switcher__continue"
          :label="continueButtonString"
          modifiers="primary block center"
          type="button"
          @click.prevent="handleSelectInstance"
        />

        <btn
          class="multi-country-switcher__alt-button"
          :label="altButtonString"
          modifiers="secondary block center"
          type="button"
          @click.prevent="handleAltButtonClick"
        />
      </div>
    </div>

    <!-- Custom overlay to bind custom close event -->
    <teleport to="body">
      <transition name="fade">
        <button
          v-if="overlay.isActive"
          class="window-overlay"
          :class="namespace"
          tabindex="-1"
          type="button"
          aria-hidden="true"
          @click.prevent="handleCloseOverlay"
        />
      </transition>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Multi country switcher (multi-country-switcher)
 * -----------------------------------------------------------------------------
 * Used to configure multi stores and triggering switcher popup.
 *
 * @param {Array} countries - List of countries can cover.
 * @param {Object} fallbackStrings - Fallback title and text.
 * @param {Array} instances - Country instances.
 *
 */
import { mapActions } from 'vuex'

import Btn from '~global/btn/btn'
import MultiCountryOptions from '~async/multi-country-switcher/multi-country-options'

import IconClose from '~icons/directional-navigation/close.svg'

export default {
  name: 'MultiCountrySwitcher',

  components: {
    Btn,
    IconClose,
    MultiCountryOptions,
  },

  props: {
    countries: {
      type: Array,
      default: () => [],
    },
    fallbackStrings: {
      type: Object,
      default: () => ({
        text: '',
        title: '',
      }),
    },
    instances: {
      type: Array,
      default: () => [],
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      namespace: 'multiCountrySwitcher',
      suggestedInstance: false,
      selectedOptions: false,
      sessionKey: 'multi-store-dismissed',
      template: 'suggestions',
    }
  },

  computed: {

    /**
     * Compute alt button string.
     * @returns {String}
     */
    altButtonString() {
      return this.template === 'suggestions'
        ? this.$string('general.multi_country.suggestions.change')
        : this.$string('general.multi_country.options.cancel')
    },

    /**
     * Compute continue button string.
     * @returns {String}
     */
    continueButtonString() {
      return this.template === 'suggestions'
        ? this.$string(
          'general.multi_country.suggestions.continue_html',
          { replace: this.replaceStrings() },
        )
        : this.$string('general.multi_country.options.confirm')
    },

    /**
     * Compute suggestion strings.
     * @returns {Object}
     */
    currentInstance() {
      return this.instances.find((instance) => {
        return instance.countryCode === this.$variable('store.country')
      })
    },

    /**
     * Compute suggestion strings.
     * @returns {Object}
     */
    suggestionStrings() {
      if (!this.suggestedInstance) {
        return this.fallbackStrings
      }

      return {
        text: this.$string(
          'general.multi_country.suggestions.text_html',
          { replace: this.replaceStrings() },
        ),
        title: this.$string(
          'general.multi_country.suggestions.title_html',
          { replace: this.replaceStrings() },
        ),
      }
    },
  },

  mounted() {
    if (sessionStorage.getItem(this.sessionKey)) {
      this.selectedOptions = JSON.parse(sessionStorage.getItem(this.sessionKey))
    }

    /**
     * If there are selected options (populated from session storage), then
     * prevent the multi-store modal from opening.
     */
    if (this.selectedOptions) {
      return
    }

    this.queryGeolocation()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Query geolocation from Shopify geolocation app.
     */
    async queryGeolocation() {
      try {
        if (cnvs.storybook) {
          this.setGeolocation({
            detected_values: {
              country: {
                handle: 'GB',
                name: 'United Kingdom',
              },
            },
          })

          return
        }

        let response = await fetch('/browsing_context_suggestions.json')
        response = await response.json()
        this.setGeolocation(response)

      } catch (error) {
        throw new Error('Failed to fetch geolocation', error)
      }
    },

    /**
     * Open multi-country switcher modal based on customer's location.
     */
    setGeolocation(response) {
      const countryCode = response.detected_values?.country?.handle
      const suggestedCountry = this.selectedOptions?.instance?.countryCode
      this.setSuggestedInstance(response.detected_values.country)

      if (
        this.countries.includes(countryCode) ||
        suggestedCountry === countryCode
      ) {
        return
      }

      this.openOverlay({
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },

    /**
     * Set suggested instance based on customer's country.
     * @param {Object} country - Current country.
     */
    setSuggestedInstance(country) {
      const theInstance = this.instances.find((instance) => {
        return instance.countryCode === country.handle
      })

      if (!theInstance) {
        this.suggestedInstance = false
        return
      }

      this.suggestedInstance = theInstance
      this.selectedOptions = {
        instance: theInstance,
        language: theInstance.languages[0]?.code,
      }
    },

    /**
     * Replace text shortcodes.
     * @returns {Object}
     */
    replaceStrings() {
      return {
        country_code: this.suggestedInstance.countryCode,
        country_name: this.suggestedInstance.country,
        currency_code: this.suggestedInstance.currency,
        currency_symbol: this.suggestedInstance.currencySymbol,
        language_name: this.suggestedInstance.languages[0]?.name,
      }
    },

    /**
     * Handle alt button click.
     */
    async handleAltButtonClick() {
      this.template = this.template === 'suggestions'
        ? 'options'
        : 'suggestions'

      await this.$nextTick()
      cnvs.EventBus.emit('overlay:update', this.namespace)
    },

    /**
     * Handle close overlay click.
     */
    handleCloseOverlay() {

      /**
       * Assign selected options to current instance if dismissed without
       * changing the country.
       */
      if (!this.selectedOptions) {
        this.selectedOptions = {
          instance: this.currentInstance,
          language: this.currentInstance.languages[0]?.code,
        }
      }

      sessionStorage.setItem(this.sessionKey, JSON.stringify(this.selectedOptions))
      this.closeOverlay(this.namespace)
    },

    /**
     * Redirects to the selected store.
     */
    handleSelectInstance() {
      sessionStorage.setItem(this.sessionKey, JSON.stringify(this.selectedOptions))
      this.closeOverlay(this.namespace)
      const domain = this.selectedOptions.instance.url
      const countryCode = this.selectedOptions.instance.countryCode.toLowerCase()
      const language = this.selectedOptions.language.toLowerCase()
      const enableLanguageSubfolder = this.selectedOptions.instance.enableLanguageSubfolder

      if (enableLanguageSubfolder) {
        location.href = `${domain}${language}-${countryCode}/`
      } else {
        location.href = domain
      }
    },

    /**
     * Update selected instance and language.
     * @param {Object} selectedOptions - instance and language
     */
    updateSelectedInstance(selectedOptions) {
      this.selectedOptions = selectedOptions
    },
  },
}
</script>

<style lang="scss">
@import './multi-country-switcher';
</style>
